import { yupResolver } from "@hookform/resolvers/yup";
import { jwtDecode } from "jwt-decode";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import React, { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import * as yup from "yup";
import {
  setUserFullName,
  setUserRmcId,
  setUserRole,
} from "../reduxToolkit/reducers/layout/authSlice";
import { store } from "../reduxToolkit/store";
import { loginService } from "../service/login.service";

// Yup is a schema builder for runtime value parsing and validation
const schema = yup.object().shape({
  email: yup
    .string()
    // Updated regex: only lowercase letters allowed and TLD must be exactly 2 or 3 letters
    .matches(
      /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,3}$/,
      "Enter a valid email address"
    )
    .required("Email is required"),
  password: yup
    .string()
    .required("Password is required")
    .min(6, "Password must be at least 6 characters long"),
});

const Login: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch(); // Add Redux dispatch
  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const hasNavigated = useRef(false); // Track navigation state

  const [isLoading, setIsLoading] = useState(false); // new state for loading
  const toggle = () => setPasswordVisible(!isPasswordVisible);

  // useForm is a custom hook for managing forms with ease, with yupResolver to validate the form schema
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onBlur",
  });

  // Optionally, get user info from redux if needed
  // const userFullName = useSelector((state: any) => state.auth.userFullName);
  // const userRole = useSelector((state: any) => state.auth.userRole);

  const onSubmit = async ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => {
    setIsLoading(true);
    try {
      const response = await loginService.login({ email, password });
      const token = response?.token;

      if (token) {
        // The loginService already dispatched the login action, so we don't need to dispatch it again
        const decodedToken: {
          navigation: string;
          "http://schemas.microsoft.com/ws/2008/06/identity/claims/role": string;
          sub: string;
          name?: string;
          fullname?: string;
          fullName?: string;
          given_name?: string;
          family_name?: string;
          rmcId?: string;
          "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"?: string;
        } = jwtDecode(token);

        const userId = decodedToken.sub;
        const role =
          decodedToken[
            "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
          ];

        dispatch(
          setUserFullName({
            given_name: decodedToken.given_name,
            family_name: decodedToken.family_name,
            fallback:
              decodedToken[
                "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"
              ] || email,
          })
        );
        dispatch(setUserRole(role));
        if (decodedToken.rmcId) {
          dispatch(setUserRmcId(decodedToken.rmcId));
        }

        console.log("Decoded Token:", decodedToken);
        console.log("After login dispatches", {
          token,
          isAuthenticated: store.getState().auth.isAuthenticated,
          userFullName: store.getState().auth.userFullName,
          userRole: store.getState().auth.userRole,
          rmcId: store.getState().auth.rmcId,
        });

        // Redirect based on user role after successful login
        let navigationPath = "/";
        if (role === "Administrator") {
          navigationPath = "/admin/dashboard";
        } else if (role === "Consultant") {
          navigationPath = "/dashboard";
        } else if (role === "B2BUser") {
          navigationPath = "/b2b-property";
        } else if (role === "RMCAdmin") {
          navigationPath = "/rmc/dashboard";
        } else if (role === "RMCUser") {
          navigationPath = "/rmc/dashboard";
        }

        if (!hasNavigated.current) {
          hasNavigated.current = true;
          navigate(navigationPath);
        }
      }
      Swal.fire({
        toast: true,
        position: "top-right",
        icon: "success",
        title: "Login successfully.",
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true,
        customClass: { popup: "swal2-toast-bottom" },
      });
    } catch (error) {
      console.error("Login error:", error);
      setIsLoading(false); // re-enable button on error
      Swal.fire({
        toast: true,
        position: "top-right",
        icon: "error",
        title: "Login failed.",
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true,
        customClass: { popup: "swal2-toast-bottom" },
      });
    }
  };

  return (
    <div className="content">
      <div className="content-body p-0">
        <div className="login-bg">
          <div className="container">
            <div className="row align-items-center min-vh-100">
              {/* Left side - Marketing Content */}
              <div className="col-lg-6 col-md-12 mb-4 mb-lg-0">
                <div
                  className="text-white"
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    backdropFilter: "blur(10px)",
                    borderRadius: "15px",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.37)",
                    paddingLeft: "2.5rem",
                    paddingRight: "2rem",
                    paddingTop: "2.5rem",
                    paddingBottom: "2.5rem",
                    overflow: "visible",
                  }}
                >
                  <div
                    className="d-flex align-items-center gap-3 mb-4"
                    style={{ overflow: "visible" }}
                  >
                    <svg
                      width="36"
                      height="36"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ flexShrink: 0 }}
                    >
                      <path
                        d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="rgba(255, 255, 255, 0.1)"
                      />
                      <path
                        d="M9 11l2 2 4-4"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <h2
                      className="fw-bold mb-0"
                      style={{ fontSize: "1.25rem", lineHeight: "1.3" }}
                    >
                      Your Trusted Partner for Corporate Housing
                    </h2>
                  </div>
                  <p
                    className="lead mb-0"
                    style={{ fontSize: "1.1rem", lineHeight: "1.8" }}
                  >
                    From apart-hotels to co-living spaces in the premium luxury
                    and budget categories, IOS offers every type of stay. We
                    ensure every stay feels exceptional. Trust us to make your
                    stay effortless, exclusive, and unforgettable.
                  </p>
                </div>
              </div>

              {/* Right side - Login Form */}
              <div className="col-lg-6 col-md-12">
                <div className="d-flex justify-content-center justify-content-lg-end">
                  <form
                    className="login-form"
                    onSubmit={handleSubmit(onSubmit)}
                  >
                    <div className="d-flex flex-column gap-2">
                      <h2 className="login-title">Sign in to your account</h2>
                      <p>Enter your email and password to login</p>
                      <div className="d-flex flex-column gap-2 form-group">
                        <label htmlFor="email">Email ID</label>

                        <InputText
                          type="email"
                          id="email"
                          placeholder="Email"
                          {...register("email")}
                        />
                      </div>
                      <div className="d-flex flex-column gap-2 form-group position-relative">
                        <label htmlFor="password">Password</label>
                        <input
                          type={isPasswordVisible ? "text" : "password"}
                          className="form-control pe-5"
                          id="password"
                          placeholder="Password"
                          {...register("password")}
                        />
                        <i
                          className={`bi ${
                            isPasswordVisible ? "bi-eye" : "bi-eye-slash"
                          } text-dark position-absolute translate-middle-y mt-5 end-0 me-3 cursor-pointer`}
                          onClick={toggle}
                          role="button"
                          aria-label={
                            isPasswordVisible
                              ? "Hide password"
                              : "Show password"
                          }
                          tabIndex={0}
                          onKeyDown={(e) => e.key === "Enter" && toggle()}
                        ></i>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="rememberMe"
                          />
                          <label
                            className="form-check-label"
                            htmlFor="rememberMe"
                          >
                            Remember me
                          </label>
                        </div>
                        <a
                          href="/forgot-password"
                          className="text-light text-link"
                        >
                          Forgot password?
                        </a>
                      </div>
                      <Button
                        type="submit"
                        label="Login"
                        className="p-button-primary"
                      />
                      <div className="text-center mt-3">
                        <span>Don't have an account? </span>
                        <a
                          href="/signup"
                          className="text-link"
                          style={{ color: "#1976d2" }}
                        >
                          Sign up
                        </a>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
